if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        this.thumbnailPrevButton = this.elements.thumbnails.querySelector('button[name="previous"]');
        this.thumbnailNextButton = this.elements.thumbnails.querySelector('button[name="next"]');
        this.thumbnailPrevButton?.addEventListener('click', this.onThumbnailArrowClick.bind(this, -1));
        this.thumbnailNextButton?.addEventListener('click', this.onThumbnailArrowClick.bind(this, 1));

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const activeMedia = event.detail.currentElement;

        // Keep the viewer's active slide in sync when the user swipes the
        // mobile slider (not just when a thumbnail is clicked). Without this,
        // `is-active` never moves off the first slide on mobile, so the
        // modal-opener overlay stays on top of video/model slides and taps
        // never reach the play button underneath it.
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (this.elements.thumbnails.isVertical && this.elements.thumbnails.isVertical()) {
          this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
          return;
        }
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      onThumbnailArrowClick(direction, event) {
        if (!this.elements.thumbnails) return;
        const isVertical = this.elements.thumbnails.isVertical && this.elements.thumbnails.isVertical();
        const hasVerticalOverflow =
          isVertical && this.elements.thumbnails.slider.scrollHeight > this.elements.thumbnails.slider.clientHeight + 1;

        // If thumbnails overflow vertically, keep native slider-button scroll behavior.
        // We only hijack arrows for short lists where there is nothing to scroll.
        if (hasVerticalOverflow) return;

        event.preventDefault();

        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        if (!thumbnails.length) return;

        let currentIndex = thumbnails.findIndex(
          (item) => item.querySelector('button')?.getAttribute('aria-current') === 'true'
        );
        if (currentIndex < 0) currentIndex = 0;

        const nextIndex = (currentIndex + direction + thumbnails.length) % thumbnails.length;
        const nextThumbnail = thumbnails[nextIndex];
        if (!nextThumbnail) return;

        this.setActiveMedia(nextThumbnail.dataset.target, false);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
